import React, { useMemo, useState } from 'react';
import Image from 'next/image';

// Project cards for the gallery (labels work)
const projects = [
  {
    id: 1,
    title: 'FMCG Snack Labels',
    description:
      'High-impact printed labels designed for standout shelf presence. Moisture resistant with durable adhesion.',
    image: '/images/portfolio/snack_label.png',
    category: 'fmcg',
    tags: ['Chips', 'Snack', 'FMCG']
  },
  {
    id: 2,
    title: 'Premium Gin Label',
    description:
      'Premium paper labels with matte finish and foil details for a refined brand look.',
    image: '/images/portfolio/gin_label.png',
    category: 'beverages',
    tags: ['Luxury', 'Basil', 'Vegan', 'Sustainable']
  },
  {
    id: 3,
    title: 'Pharma Compliance Set',
    description:
      'Multi-SKU set with variable data and barcodes for complete product traceability.',
    image: '/images/portfolio/pharma_labels_royal_prime.png',
    category: 'pharma',
    tags: ['Medical', 'Barcode', 'Compliance']
  },
  {
    id: 4,
    title: 'Industrial Oil Labels',
    description:
      'Oil-resistant labels with strong adhesion tailored for industrial packaging.',
    image: '/images/portfolio/industrial_oil_label.png',
    category: 'industrial',
    tags: ['Logistics', 'Durable']
  },
  {
    id: 5,
    title: 'Luxury Cosmetic Foil',
    description:
      'Embossed cosmetics labels with metallic accents for premium positioning.',
    image: '/images/portfolio/luxury_cosmetic_foil.png',
    category: 'cosmetics',
    tags: ['Luxury', 'Foil', 'Matte']
  },
  {
    id: 6,
    title: 'Food-Safe Retail Labels',
    description:
      'Food-safe adhesive labels with nutrition panels and scratch resistance.',
    image: '/images/portfolio/food_safe_retail_label.png',
    category: 'fmcg',
    tags: ['Food', 'Retail']
  },
  {
    id: 7,
    title: 'Luxury Wine Film',
    description:
      'Clear-on-clear film labels for a “no-label” premium look on bottles.',
    image: '/images/portfolio/luxury_wine_film.png',
    category: 'beverages',
    tags: ['Luxury', 'Bottle', 'Clear Film']
  },
  {
    id: 8,
    title: 'Logistics UV Labels',
    description:
      'UV-coated labels for enhanced durability during cold-chain logistics.',
    image: '/images/portfolio/logistics_uv_label.png',
    category: 'logistics',
    tags: ['Cold Chain', 'UV Coat']
  },
  {
    id: 9,
    title: 'Sustainable Eco Labels',
    description:
      'Biodegradable and compostable labels designed with FSC-certified papers for eco-conscious brands.',
    image: '/images/portfolio/sustainable_eco_label.png',
    category: 'fmcg',
    tags: ['Sustainable', 'Recyclable', 'Eco-Friendly']
  }
];

const categoryFilters = [
  { id: 'all', name: 'All' },
  { id: 'fmcg', name: 'FMCG' },
  { id: 'beverages', name: 'Beverages' },
  { id: 'pharma', name: 'Pharma' },
  { id: 'cosmetics', name: 'Cosmetics' },
  { id: 'industrial', name: 'Industrial' },
  { id: 'logistics', name: 'Logistics' }
];

const WorkAndInsightsGallery = () => {
  const [activeCategory, setActiveCategory] = useState('all');

  const filtered = useMemo(() => {
    return projects.filter((p) => {
      return activeCategory === 'all' || p.category === activeCategory;
    });
  }, [activeCategory]);

  return (
    <section className="max-w-[1300px] mx-auto px-4 lg:px-0 py-16 md:py-24">
      {/* Heading */}
      <div className="text-center mb-6 md:mb-8">
        <h2 className="text-[28px] md:text-[36px] font-bold text-[#112140]">
          Labels That Speak for Themselves
        </h2>
        <p className="text-[16px] md:text-[18px] text-[#475467] max-w-[820px] mx-auto mt-3">
          From FMCG to Pharma, discover how our end-to-end solutions deliver impact across industries.
        </p>
      </div>

      {/* Category filters (chips) */}
      <div className="flex overflow-x-auto lg:flex-wrap lg:justify-center gap-2 md:gap-3 mb-10 pb-2 no-scrollbar scroll-smooth">
        {categoryFilters.map((c) => (
          <button
            key={c.id}
            onClick={() => setActiveCategory(c.id)}
            className={`whitespace-nowrap px-4 md:px-5 py-2 rounded-full text-[14px] md:text-[15px] font-medium transition-colors border ${activeCategory === c.id
              ? 'bg-[#112140] text-white border-[#112140]'
              : 'bg-white text-[#344054] border-[#D0D5DD] hover:bg-[#F9FAFB]'
              }`}
          >
            {c.name}
          </button>
        ))}
      </div>

      {/* Cards grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
        {filtered.map((item) => (
          <div key={item.id} className="bg-white rounded-2xl border border-[#E4E7EC] shadow-sm hover:shadow-md transition-shadow">
            <div className="relative w-full h-[220px] md:h-[240px] overflow-hidden bg-slate-50 rounded-t-2xl flex items-center justify-center">
              <Image src={item.image} alt={item.title} fill className="object-contain p-2" />
            </div>
            <div className="p-5 md:p-6">
              <div className="flex flex-wrap gap-2 mb-2">
                {item.tags?.slice(0, 3).map((tag, i) => (
                  <span key={i} className="px-2.5 py-1 text-[12px] rounded-full bg-[#EEF2F6] text-[#344054]">
                    {tag}
                  </span>
                ))}
              </div>
              <h3 className="text-[18px] md:text-[20px] font-semibold text-[#112140]">{item.title}</h3>
              <p className="mt-2 text-[14px] md:text-[15px] text-[#475467] leading-6">
                {item.description}
              </p>
              <a href="#" className="mt-4 inline-flex items-center text-[#1A5AFF] font-medium text-[14px] md:text-[15px]">
                Know More
                <svg className="ml-2" width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M4.16675 10H15.8334M15.8334 10L10.8334 5M15.8334 10L10.8334 15" stroke="#1A5AFF" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </a>
            </div>
          </div>
        ))}
      </div>

      {filtered.length === 0 && (
        <div className="text-center py-12">
          <p className="text-[16px] text-[#667085]">No results found. Try a different filter.</p>
        </div>
      )}
    </section>
  );
};

export default WorkAndInsightsGallery;
